import styles from "./NotificationMenu.module.scss";
import { Card, ComponentLoader, Span, StyledLink, Chevron, Button, height, ExternalLink, TextWithOverflow } from "@netapp/bxp-design-system-react";
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import classNames from "classnames";
import { formatDistanceToNow } from "date-fns";
import { RemoveDarkIcon as RemoveIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import { CriticalNotificationIcon, ErrorIcon, BellIcon, InfoIcon, LightBulbIcon, NoticeTriangleIcon, SuccessIcon } from "@netapp/bxp-style/react-icons/Notification"
import { UnmountClosed } from 'react-collapse';
import { FilterServices, FilterType } from "./NotificationsFilters";
import _ from "lodash";
import { useTranslation } from 'react-i18next';
import { allMenus as i18nNamespace } from "../../../i18n/namespace";
import { getSupportedServicesOptions } from "utils/notificationsUtils";
import { VariableSizeList as VariableList } from 'react-window';

const iconsMap = {
    "warning": NoticeTriangleIcon,
    "error": ErrorIcon,
    "info": InfoIcon,
    "success": SuccessIcon,
    "recommendation": LightBulbIcon,
    "critical": CriticalNotificationIcon
}

const DismissingAllOverlay = () => {
    const { t } = useTranslation(i18nNamespace);
    return <div className={styles['dismissing-all']}>
        <ComponentLoader style={{ marginBottom: 14 }} />
        <Span level={14} bold>{t('dismissing_notification')}</Span>
    </div>
}

const ESTIMATED_NOTIFICATION_HEIGHT = 190;
const ALERT_DETAILS_LABEL = 'Alert-Details';

const Notification = ({ label, priority, timestamp, serviceName, stored, service, id, cta, descriptions, link, onExpandToggle }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(i18nNamespace);
    const isDescriptionAlwaysOpen = priority === "critical"
    const [isDescriptionOpen, setDescriptionOpen] = useState(isDescriptionAlwaysOpen);
    let Icon = iconsMap[priority];
    const ctaLabel = cta?.label === ALERT_DETAILS_LABEL ? t('view_alert') : cta?.label;
    return <Card key={id} className={styles[`notification-container-${priority}`]}>
        <div className={classNames(styles[`icon-container-${priority}`])}>
            <Icon className={classNames(styles['priority-icon'], styles[`priority-icon-${priority}`], styles[priority])} />
        </div>
        <div className={styles['notification-body']}>

            <div className={classNames(styles['label'])}>
                <div className={classNames(styles[priority], priority !== "critical" && styles["limited-header"])}>
                    <TextWithOverflow tooltipContent={label}>{label}</TextWithOverflow>
                </div>
            </div>
            <UnmountClosed isOpened={isDescriptionOpen} theme={{ collapse: height, content: styles['descriptions'] }}>
                {descriptions.map((description) => <p title={description} className={styles["p-desc"]} key={description}><Span>{description}</Span></p>)}
                {link?.url && <div className={styles['link-container']}><ExternalLink variant="text" href={link.url} includeArrow={true} onClick={() => {
                    dispatch({
                        type: "TRACKING",
                        payload: {
                            category: "UiNotifications",
                            action: "FlowAction:LinkClick",
                            label: link.url,
                        }
                    })

                }}> {link.label} </ExternalLink></div>}
            </UnmountClosed>
            {ctaLabel && <div>
                <StyledLink title={ctaLabel} className={styles['cta']} color="secondary" to={{ pathname: new URL(cta.to, window.location.origin).pathname, search: new URL(cta.to, window.location.origin).search, state: cta.params }} onClick={() => {
                    dispatch({
                        type: "TRACKING",
                        payload: {
                            category: "UiNotifications",
                            action: "FlowAction:CallToActionClick",
                            label,
                        }
                    })
                    dispatch({
                        type: "MENU:CLOSE-WIDGET"
                    })
                }}>{ctaLabel}</StyledLink>
            </div>}
            <div className={styles['notification-info']}>
                <div><span className={styles['value']}>{t("time_ago", { time: formatDistanceToNow(timestamp) })}</span><span title={serviceName || service} className={styles['value']}>{serviceName || service}</span></div>
                <Button className={styles['dismiss-one']} type="text" onClick={(e) => {
                    dispatch({
                        type: "UI-NOTIFICATIONS:DISMISS-ONE",
                        payload: {
                            id,
                            stored
                        },
                        track: label
                    })
                    e.stopPropagation();
                }}><RemoveIcon className={classNames(styles['dismiss-one-icon'])} />{t('dismiss')}</Button>
            </div>
        </div>
        <div className={styles['chevron-slot']}>
            {(descriptions && descriptions.length > 0 && !isDescriptionAlwaysOpen) && <button onClick={() => {
                setDescriptionOpen(!isDescriptionOpen)
                const flowAction = !isDescriptionOpen ? "FlowAction:OpenDescription" : "FlowAction:CloseDescription"
                dispatch({
                    type: "TRACKING",
                    payload: {
                        category: "UiNotifications",
                        action: flowAction,
                        label,
                    },
                })

                if (onExpandToggle) {
                    onExpandToggle();
                }

            }}><div className={styles['chevron']}><Chevron isActive={isDescriptionOpen} color="secondary" /></div></button>}
        </div>
    </Card>
}

const NotificationsContent = React.memo(() => {
    const { t } = useTranslation(i18nNamespace);
    const notifications = useSelector(state => state.uiNotifications.notifications);
    const servicesFilter = useSelector(state => state.uiNotifications.servicesFilter);
    const typeFilter = useSelector(state => state.uiNotifications.typeFilter);
    const isDismissingAll = useSelector(state => state.uiNotifications.isDismissingAll);
    const services = useSelector(state => state.app.services);
    const servicesList = useSelector(state => state.app.servicesList);
    const supportedNotificationServices = getSupportedServicesOptions(servicesList);
    const dispatch = useDispatch();

    const filteredNotifications = useMemo(() => {
        if (!typeFilter.activeCount && !servicesFilter.activeCount) {
            return notifications;
        }

        return _.filter(notifications, notification => {
            const matchesType = !typeFilter.activeCount || typeFilter.values[notification.priority] || typeFilter.values['all']
            const matchesService = !servicesFilter.activeCount || servicesFilter.values[notification.service] || servicesFilter.values['all']
            return matchesType && matchesService;
        })
    }, [notifications, typeFilter, servicesFilter]);

    const listRef = useRef(null);
    const rowHeightsRef = useRef({});
    const notificationsContainerRef = useRef(null);
    const [listHeight, setListHeight] = useState(600);

    useEffect(() => {
        if (!notificationsContainerRef.current) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            setListHeight(entry.contentRect.height);
        });
        observer.observe(notificationsContainerRef.current);
        return () => observer.disconnect();
    }, [notifications, filteredNotifications]);

    const getRowHeight = useCallback(index => {
        return rowHeightsRef.current[index] || ESTIMATED_NOTIFICATION_HEIGHT;
    }, []);

    const setRowHeight = useCallback((index, size) => {
        const currentSize = rowHeightsRef.current[index];
        if (currentSize === size) {
            return;
        }
        rowHeightsRef.current = {
            ...rowHeightsRef.current,
            [index]: size,
        };
        listRef.current?.resetAfterIndex(index, true);
    }, []);

    useEffect(() => {
        rowHeightsRef.current = {};
        listRef.current?.resetAfterIndex(0, false);
    }, [filteredNotifications]);

    const NotificationRow = ({ index, style }) => {
        const notification = filteredNotifications[index];
        const rowRef = useRef(null);

        useEffect(() => {
            if (!rowRef.current) {
                return;
            }

            const measure = () => {
                if (!rowRef.current) {
                    return;
                }
                const measuredHeight = Math.ceil(rowRef.current.getBoundingClientRect().height + 10);
                setRowHeight(index, measuredHeight);
            };

            measure();

            if (typeof ResizeObserver === "undefined") {
                return;
            }

            const observer = new ResizeObserver(() => {
                measure();
            });

            observer.observe(rowRef.current);
            return () => observer.disconnect();
        }, [index, notification.id, setRowHeight]);

        const handleExpandToggle = useCallback(() => {
            if (!rowRef.current) {
                return;
            }
            // Force an immediate measurement after toggle to ensure list updates
            const measuredHeight = Math.ceil(rowRef.current.getBoundingClientRect().height + 10);
            setRowHeight(index, measuredHeight);
        }, [index, setRowHeight]);

        return (
            <div style={{
                ...style,
            }}>
                <div ref={rowRef} style={{ paddingBottom: 10, overflow: "visible" }}>
                    <Notification
                        key={notification.id}
                        {...notification}
                        onExpandToggle={handleExpandToggle}
                        serviceName={services[notification.service]?.name ??
                            supportedNotificationServices.find(s => _.toLower(s.value) === _.toLower(notification.service))?.label}
                    />
                </div>
            </div>
        );
    };

    if (notifications.length === 0) {
        return <div className={styles['no-notifications']}>
            <BellIcon color="disabled" size={36} style={{ marginBottom: 14 }} />
            <Span level={14} bold>{t('no_new_notifications_this_season')}</Span>
        </div>
    }

    return <div className={styles['content']}>
        {isDismissingAll && <DismissingAllOverlay />}
        <div className={styles["widgets"]}>
            <FilterServices />
            <FilterType />
            {(typeFilter.activeCount > 0 || servicesFilter.activeCount > 0) && <Button type="text" onClick={(e) => {
                dispatch({
                    type: "UI-NOTIFICATIONS:CLEAR-FILTERS"
                })
                e.stopPropagation();
            }}>{t('clear_filters')}</Button>}
        </div>
        {filteredNotifications.length === 0 && <div className={styles['no-notifications']}>
            <BellIcon color="disabled" size={36} style={{ marginBottom: 14 }} />
            <Span level={14} bold>{t('no_notifications_current_filter')}</Span>
        </div>}
        {filteredNotifications.length > 0 && <div ref={notificationsContainerRef} className={styles["notifications"]}>
            <VariableList
                ref={listRef}
                height={listHeight}
                itemCount={filteredNotifications.length}
                itemSize={getRowHeight}
                itemKey={index => filteredNotifications[index].id}
                overscanCount={5}
                width="100%"
            >
                {NotificationRow}
            </VariableList>
        </div>}
    </div>
})

export default NotificationsContent;